import { usePocket } from "@/contexts/PocketContext";
import { useEffect, useState } from "react";
import useSWR from "swr";

export interface Agent {
  id: string;
  code: string;
  name: string;
  company?: string;
  phone?: string;
  email?: string;
  uniqueSlug: string;
  createdBy: string;
  created: string;
  updated: string;
  expand?: {
    createdBy?: {
      id: string;
      email: string;
      name?: string;
    };
  };
}

export function useAgents() {
  const { pb, user } = usePocket();
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [loadingProgress, setLoadingProgress] = useState({ loaded: 0, total: 0 });

  const {
    data,
    mutate,
    error: swrError,
  } = useSWR<Agent[]>(
    user ? `agents-${user.id}` : null,
    async () => {
      if (!user) return [];
      let filter: string | undefined = undefined;
      if (!user.isSuperAdmin) {
        filter = `createdBy = "${user.id}"`;
      }
      
      const allRecords: Agent[] = [];
      const pageSize = 20;
      let page = 1;
      let hasMore = true;
      let totalItems = 0;
      
      setLoadingProgress({ loaded: 0, total: 0 });
      
      while (hasMore) {
        try {
          const result = await pb.collection("agents").getList<Agent>(page, pageSize, {
            ...(filter ? { filter } : {}),
            fields: "id,code,name,company,phone,email,uniqueSlug,created,updated,createdBy",
            sort: "-created",
            ...(user.isSuperAdmin ? { expand: "createdBy" } : {}),
          });
          
          allRecords.push(...result.items);
          if (result.totalItems > 0) {
            totalItems = result.totalItems;
          }
          setLoadingProgress({ loaded: allRecords.length, total: totalItems || allRecords.length });
          
          hasMore = result.items.length === pageSize && (totalItems === 0 || allRecords.length < totalItems);
          page++;
        } catch (err: any) {
          console.error("Error loading agents page:", err);
          hasMore = false;
          if (allRecords.length === 0) {
            throw err;
          }
        }
      }
      
      setLoadingProgress({ loaded: allRecords.length, total: allRecords.length });
      return allRecords;
    },
    {
      revalidateOnFocus: false,
      revalidateOnReconnect: true,
      dedupingInterval: 5000,
    },
  );

  useEffect(() => {
    if (swrError) {
      setError(swrError);
      setIsLoading(false);
      setLoadingProgress({ loaded: 0, total: 0 });
    } else if (data !== undefined) {
      setIsLoading(false);
      setError(null);
      setLoadingProgress({ loaded: data.length, total: data.length });
    }
  }, [data, swrError]);

  const createAgent = async (data: {
    code: string;
    name: string;
    company?: string;
    phone?: string;
    email?: string;
    uniqueSlug: string;
  }) => {
    if (!user) throw new Error("User not authenticated");
    try {
      const payload: any = {
        code: data.code,
        name: data.name,
        uniqueSlug: data.uniqueSlug,
      };
      if (data.company) payload.company = data.company;
      if (data.phone) payload.phone = data.phone;
      if (data.email) payload.email = data.email;
      if (user.id) {
        payload.createdBy = user.id;
      }
      console.log("Sending to PocketBase:", payload);
      console.log("User ID:", user.id, "User object:", user);
      const record = await pb.collection("agents").create<Agent>(payload);
      await mutate();
      return record;
    } catch (error: any) {
      console.error("PocketBase error details:", {
        message: error.message,
        status: error.status,
        response: error.response,
        data: error.response?.data,
        fullError: error,
      });
      if (error.response?.data) {
        const errorData = error.response.data;
        console.error("Error data:", JSON.stringify(errorData, null, 2));
        if (errorData.data) {
          const fieldErrors = Object.entries(errorData.data)
            .map(([field, errors]: [string, any]) => {
              if (typeof errors === "object" && errors.code === "validation_not_unique") {
                return `${field}: Значение должно быть уникальным`;
              }
              return `${field}: ${Array.isArray(errors) ? errors.join(", ") : typeof errors === "object" ? errors.message || JSON.stringify(errors) : errors}`;
            })
            .join("; ");
          console.error("Field errors:", fieldErrors);
          throw new Error(`Ошибка валидации: ${fieldErrors}`);
        }
        if (errorData.message) {
          throw new Error(errorData.message);
        }
      }
      throw error;
    }
  };

  const updateAgent = async (id: string, data: Partial<Agent>) => {
    const record = await pb.collection("agents").update<Agent>(id, data);
    await mutate();
    return record;
  };

  const deleteAgent = async (id: string) => {
    try {
      // Проверяем, есть ли связанные продажи
      const sales = await pb.collection("sales").getFullList({
        filter: `agent = "${id}"`,
        fields: "id",
      });

      if (sales.length > 0) {
        throw new Error(
          `Невозможно удалить агента: у него есть ${sales.length} связанных продаж. Сначала удалите или переназначьте продажи.`,
        );
      }

      await pb.collection("agents").delete(id);
      await mutate();
    } catch (err: unknown) {
      const error = err as { message?: string; response?: { data?: { message?: string } } };
      if (error.message) {
        throw new Error(error.message);
      }
      if (error.response?.data?.message) {
        throw new Error(error.response.data.message);
      }
      throw new Error("Ошибка при удалении агента. Возможно, у него есть связанные записи.");
    }
  };

  return {
    agents: data || [],
    isLoading,
    loadingProgress,
    error,
    createAgent,
    updateAgent,
    deleteAgent,
    refresh: mutate,
  };
}

export function useAgent(id: string | null) {
  const { pb, user } = usePocket();
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const {
    data,
    mutate,
    error: swrError,
  } = useSWR<Agent | null>(
    id && user ? `agent-${id}-${user.id}` : null,
    async () => {
      if (!id || !user) return null;
      try {
        const record = await pb.collection("agents").getOne<Agent>(id, {
          expand: "createdBy",
        });
        if (!user.isSuperAdmin && record.createdBy !== user.id) {
          throw new Error("Доступ запрещен");
        }
        return record;
      } catch (err: any) {
        if (err.status === 404 || err.message === "Доступ запрещен") {
          throw new Error("Агент не найден или доступ запрещен");
        }
        throw err;
      }
    },
    {
      revalidateOnFocus: false,
    },
  );

  useEffect(() => {
    if (swrError) {
      setError(swrError);
      setIsLoading(false);
    } else if (data !== undefined) {
      setIsLoading(false);
      setError(null);
    }
  }, [data, swrError]);

  return {
    agent: data || null,
    isLoading,
    error,
    refresh: mutate,
  };
}

export function useAgentBySlug(slug: string | null) {
  const { pb } = usePocket();
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const {
    data,
    mutate,
    error: swrError,
  } = useSWR<Agent | null>(
    slug ? `agent-slug-${slug}` : null,
    async () => {
      if (!slug) return null;
      try {
        console.log("Fetching agent by slug:", slug);
        const records = await pb.collection("agents").getFullList<Agent>({
          filter: `uniqueSlug = "${slug}"`,
          expand: "createdBy",
          limit: 1,
        });
        console.log("Agent found:", records[0]);
        return records[0] || null;
      } catch (err: any) {
        console.error("Error fetching agent by slug:", err);
        console.error("Error details:", {
          status: err.status,
          message: err.message,
          response: err.response,
          data: err.response?.data,
        });
        if (err.status === 401 || err.status === 403) {
          try {
            const records = await pb.collection("agents").getFullList<Agent>({
              filter: `uniqueSlug = "${slug}"`,
              limit: 1,
            });
            return records[0] || null;
          } catch (retryErr: any) {
            console.error("Retry failed:", retryErr);
            throw retryErr;
          }
        }
        throw err;
      }
    },
    {
      revalidateOnFocus: false,
    },
  );

  useEffect(() => {
    if (swrError) {
      setError(swrError);
      setIsLoading(false);
    } else if (data !== undefined) {
      setIsLoading(false);
      setError(null);
    }
  }, [data, swrError]);

  return {
    agent: data || null,
    isLoading,
    error,
    refresh: mutate,
  };
}
